import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff, MergeType } from "@mui/icons-material";
import { axiosService } from "shared/helpers/axios";

const STORAGE_KEY = "oauth_merge_dismissed";

/**
 * Dialog shown after an OAuth login creates a new account.
 * Lets the user optionally merge it into an existing password-based
 * account by proving ownership of that account.
 *
 * Props:
 *   open      — boolean
 *   onMerged  — called after a successful merge (no args); caller should navigate away
 *   onDismiss — called when user chooses to keep the new account; caller should navigate away
 */
const MergeAccountDialog = ({ open, onMerged, onDismiss }) => {
  const [existingEmail, setExistingEmail] = useState("");
  const [existingPassword, setExistingPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleMerge = async () => {
    setError(null);
    setLoading(true);
    try {
      await axiosService.post("/auth/merge-accounts/", {
        existing_email: existingEmail,
        existing_password: existingPassword,
      });
      // Always mark dismissed after a successful merge — nothing left to merge.
      localStorage.setItem(STORAGE_KEY, "1");
      // Let the caller do a full-page reload so the new auth cookies
      // (set in the merge response) are guaranteed to be in effect.
      onMerged();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Merge failed. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (remember) {
      localStorage.setItem(STORAGE_KEY, "1");
    }
    onDismiss();
  };

  const canSubmit =
    existingEmail.trim().length > 0 &&
    existingPassword.length > 0 &&
    !loading;

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <MergeType sx={{ color: "rgba(118, 75, 162, 0.85)" }} />
        Already have an account?
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          We created a new account for you. If you already have an existing
          Otable account, you can merge them by entering its credentials below.
          Your data, connections, and settings will be preserved.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Existing account email"
            type="email"
            value={existingEmail}
            onChange={(e) => setExistingEmail(e.target.value)}
            autoComplete="username"
            size="small"
            fullWidth
            disabled={loading}
          />

          <TextField
            label="Existing account password"
            type={showPassword ? "text" : "password"}
            value={existingPassword}
            onChange={(e) => setExistingPassword(e.target.value)}
            autoComplete="current-password"
            size="small"
            fullWidth
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              size="small"
              sx={{ color: "rgba(118, 75, 162, 0.6)" }}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Don't ask me again on future logins
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="text"
          onClick={handleSkip}
          disabled={loading}
          sx={{ color: "text.secondary", textTransform: "none" }}
        >
          I'm a new user / Proceed without merging
        </Button>
        <Button
          variant="contained"
          onClick={handleMerge}
          disabled={!canSubmit}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <MergeType />
            )
          }
          sx={{
            bgcolor: "rgba(118, 75, 162, 0.85)",
            "&:hover": { bgcolor: "rgba(118, 75, 162, 1)" },
            textTransform: "none",
          }}
        >
          {loading ? "Merging…" : "Merge accounts"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { STORAGE_KEY as MERGE_DISMISSED_KEY };
export default MergeAccountDialog;
